import type { SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight';
import { filter, isTruthy, partition, prop } from 'remeda';

import type { Menu, MenuItem } from './types';

export const isMenu = (el: MenuItem | Menu): el is Menu => 'parent' in el && 'items' in el;

export const doesLocationBelongToPath = ({
  locationPath,
  path,
}: {
  locationPath: string;
  path: string;
}): boolean => locationPath === path || locationPath.startsWith(`${path}/`);

/**
 * Finds the relevant menu or submenu based on the given location path.
 * Used to display the correct menu when the app loads on a specific page other than the dashboard
 */
export const findMenuForLocation = (menu: Menu, locationPath: string): Menu | null => {
  const [submenus, navItems] = partition(menu.items, isMenu);

  const pathsOfItemsInMenu = filter(navItems.map(prop('path')), isTruthy);
  const isLocationInMenu = pathsOfItemsInMenu.some(path =>
    doesLocationBelongToPath({ locationPath, path }),
  );
  if (isLocationInMenu) return menu;

  for (const submenu of submenus) {
    if (submenu.basePath && locationPath.startsWith(`${submenu.basePath}/`)) return submenu;
    const parentMenu = findMenuForLocation(submenu, locationPath);
    if (parentMenu) return parentMenu;
  }

  return null;
};

export function menuToSpotlightActions(
  menu: Menu,
  navigate: (url: string) => void,
  menuLabels: Array<string> = [],
): Array<SpotlightActionData | SpotlightActionGroupData> {
  const newMenuLabels = [...menuLabels, menu.label];
  return menu.items.reduce(
    (acc, item) => {
      if (isMenu(item)) return [...acc, ...menuToSpotlightActions(item, navigate, newMenuLabels)];

      if (item.isHidden || item.showUpsell) return acc;

      return [
        ...acc,
        {
          id: `${menu.label}-${item.label}`,
          label: item.label,
          description: newMenuLabels
            .map(label => (label === 'root' ? 'Main menu' : label))
            .join(' > '),
          onClick: () => navigate(item.path ?? ''),
        },
      ];
    },
    [] as Array<SpotlightActionData | SpotlightActionGroupData>,
  );
}
